"use client"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { Separator } from "@repo/ui/components/ui/separator"
import { cn } from "@repo/ui/lib/utils"
import {
  ArrowRight,
  Database,
  ExternalLink,
  MapPin,
  Sparkles,
  X,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import React, { useCallback, useEffect, useState } from "react"
import { HOME_LOGO_CLASS_INLINE } from "./home-marketing-content"
import {
  DENDROCARE_SITE_URL,
  navigateToPricingTab,
  type PricingTabValue,
} from "./pricing-anchors"
import { HOME_DEMO_VIDEO_SRC, ServiceVideo } from "./service-video"

interface ServiceDetailsSectionProps {
  content: Record<string, string>
}

interface ServiceFeature {
  title: string
  description: string
  icon: LucideIcon
  badge?: string
}

interface MergedServiceItem {
  type: "merged"
  logos: { src: string; alt: string }[]
  title: string
  description: string
  accordionLabel: string
  dendromapFeatures: ServiceFeature[]
  allFeatures: ServiceFeature[]
  videoSrc: string
  videoPoster: string
  pricingShortcuts: { label: string; tab: PricingTabValue }[]
  buttonText: string
  explore: React.ReactNode
}

interface StandardServiceItem {
  type: "standard"
  iconSrc: string
  title: string
  description: string
  features: ServiceFeature[]
  buttonText: string
  ctaExternalLabel: string
  ctaExternalUrl: string
  explore: React.ReactNode
  imageSrc: string
  imageAlt: string
  reverse?: boolean
}

type ServiceItem = MergedServiceItem | StandardServiceItem

interface FeatureCardProps {
  feature: ServiceFeature
}

function FeatureCard({ feature }: FeatureCardProps): JSX.Element {
  const Icon = feature.icon

  return (
    <div className="group relative rounded-xl border border-border bg-card p-4 shadow-sm transition-all duration-300 hover:border-primary/25 hover:shadow-forest">
      <div className="flex items-start gap-3">
        <div className="shrink-0">
          <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
            <Icon className="size-4 text-primary" aria-hidden />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-semibold text-sm text-foreground transition-colors group-hover:text-primary">
              {feature.title}
            </h4>
            {feature.badge ? (
              <Badge variant="secondary" className="text-xs">{feature.badge}</Badge>
            ) : null}
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {feature.description}
          </p>
        </div>
      </div>
    </div>
  )
}

export function ServiceDetailsSection({
  content,
}: ServiceDetailsSectionProps): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedService, setSelectedService] = useState<number | null>(null)

  // Lock body scroll while modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isModalOpen])

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen) handleCloseModal()
    },
    [isModalOpen],
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  const dendromapFeatures: ServiceFeature[] = [
    {
      title: "Cartographie précise et interactive",
      description:
        "Localisez et identifiez des arbres en temps réel grâce au GPS. Gérez depuis votre bureau votre base de données intégrée, pour un suivi clair et accessible depuis votre tableau de bord personnalisable.",
      icon: MapPin,
    },
    {
      title: "Gestion et suivi simplifiés",
      description:
        "Centralisez toutes les informations (essence, âge, état sanitaire, interventions, alertes) afin d'optimiser la gestion de vos arbres. Imprimez vos fiches arbres et interventions générées automatiquement.",
      icon: Database,
    },
  ]

  const dendromapExplore = (
    <div className="flex flex-col gap-4">
      <p className="leading-relaxed text-muted-foreground text-sm">
        La protection des arbres et des riverains est primordiale. Il est donc impératif de
        connaître précisément votre patrimoine arboré afin de prévenir tout risque de chute
        d&apos;arbres.
      </p>
      <p className="leading-relaxed text-muted-foreground text-sm">
        Facile à utiliser car développée avec l&apos;aide de professionnels métiers, Dendromap vous
        aide à cartographier votre parc en très peu de temps.
      </p>
      <h4 className="mt-2 text-sm font-semibold text-foreground">
        Points forts de la solution :
      </h4>
      <ul className="ml-4 list-inside list-disc space-y-1">
        {[
          "Inventaire rapide des arbres isolés et d'alignement",
          "Vue satellite précise et fiable",
          "Mise à jour facile et en temps réel des inventaires",
          "Diagnostic de l'état sanitaire et des risques de rupture",
          "Préconisations de travaux d'élagage et de maintenance",
          "Estimation de sa valeur (selon le BEVA)",
          "Compte rendu synthétique pour chaque sujet",
          "Stockage des informations sur une plateforme dédiée",
        ].map((item) => (
          <li key={item} className="text-sm text-muted-foreground">
            {item}
          </li>
        ))}
      </ul>
    </div>
  )

  const advancedExplore = (
    <div className="flex flex-col gap-4">
      <h4 className="text-sm font-semibold text-foreground">
        DENDROMAP Advanced+ — La gestion intelligente des arbres, alignements et espaces boisés
      </h4>
      <p className="leading-relaxed text-muted-foreground text-sm">
        DENDROMAP Advanced+ est bien plus qu&apos;un simple outil de gestion d&apos;arbres :
        c&apos;est une solution complète, évolutive et professionnelle conçue pour offrir une vision
        globale et précise de l&apos;ensemble du patrimoine arboré.
      </p>
      <ul className="ml-4 list-inside list-disc space-y-1">
        {[
          "Les arbres d'alignement",
          "Les espaces boisés",
          "Les zones forestières urbaines",
          "Et tout type de groupement arboré",
        ].map((item) => (
          <li key={item} className="text-sm text-muted-foreground">
            {item}
          </li>
        ))}
      </ul>
    </div>
  )

  const services: { items: ServiceItem[] } = {
    items: [
      {
        type: "merged",
        logos: [
          { src: "/images/logos/dendromap.svg", alt: "Dendromap" },
          { src: "/images/logos/dendro-advanced.svg", alt: "Dendromap Advanced+" },
        ],
        title:
          content["services.merged.title"] || "Dendromap & Dendromap Advanced+",
        description:
          content["services.merged.description"] ||
          content["services.item1.description"] ||
          `Une appli innovante qui cartographie vos arbres en un clin d'œil, pour une gestion simple, rapide et durable. De l'inventaire de base aux alignements et espaces boisés : choisissez la formule adaptée à votre patrimoine arboré.`,
        accordionLabel:
          content["services.merged.accordion"] || "Découvrir les fonctionnalités",
        dendromapFeatures,
        // Fix: include all features for the detail modal
        allFeatures: [...dendromapFeatures],
        videoSrc: HOME_DEMO_VIDEO_SRC,
        videoPoster: "/images/Dendro-1.jpg",
        pricingShortcuts: [
          {
            label: content["services.pricing.basic"] || "Voir les forfaits Dendromap",
            tab: "basic",
          },
          {
            label: content["services.pricing.advanced"] || "Voir les forfaits Advanced+",
            tab: "advanced",
          },
        ],
        buttonText: content["services.item1.buttonText"] || "En savoir plus",
        explore: (
          <div className="flex flex-col gap-6">
            {dendromapExplore}
            <Separator />
            {advancedExplore}
          </div>
        ),
      },

    ],
  }

  const handleButtonClick = (serviceIndex: number): void => {
    setSelectedService(serviceIndex)
    setIsModalOpen(true)
  }

  const handleCloseModal = (): void => {
    setIsModalOpen(false)
    setSelectedService(null)
  }

  const selectedItem =
    selectedService !== null ? services.items[selectedService] : null

  const selectedFeatures: ServiceFeature[] =
    selectedItem?.type === "merged"
      ? selectedItem.allFeatures
      : selectedItem?.type === "standard"
        ? selectedItem.features
        : []

  return (
    <section id="services" className="bg-muted py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-20">
        {services.items.map((service, index) => {
          if (service.type === "merged") {
            return (
              <div
                key="merged-dendromap"
                className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16"
              >
                <div className="flex flex-col gap-6">
                  <div className="flex flex-wrap items-center gap-4">
                    {service.logos.map((logo) => (
                      <img
                        key={logo.src}
                        src={logo.src}
                        alt={logo.alt}
                        className={HOME_LOGO_CLASS_INLINE}
                      />
                    ))}
                  </div>
                  <h3 className="text-3xl font-semibold tracking-tight text-foreground">
                    {service.title}
                  </h3>
                  <p
                    className="text-base text-muted-foreground leading-[1.75]"
                    dangerouslySetInnerHTML={{ __html: service.description }}
                  />
                  <Accordion type="single" collapsible defaultValue="features" className="w-full">
                    <AccordionItem value="features">
                      <AccordionTrigger className="text-left text-sm font-semibold hover:text-primary">
                        {service.accordionLabel}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col gap-3 pt-2">
                          {service.dendromapFeatures.map((feature) => (
                            <FeatureCard key={feature.title} feature={feature} />
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    {service.pricingShortcuts.map((shortcut) => (
                      <Button
                        key={shortcut.tab}
                        type="button"
                        variant="outline"
                        className="hover-shimmer"
                        onClick={() => navigateToPricingTab(shortcut.tab)}
                      >
                        {shortcut.label}
                        <ArrowRight className="size-4" aria-hidden data-icon="inline-end" />
                      </Button>
                    ))}
                    <Button
                      className="hover-shimmer shadow-forest transition-all hover:scale-[1.02]"
                      onClick={() => handleButtonClick(index)}
                    >
                      {service.buttonText}
                    </Button>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-video overflow-hidden rounded-2xl bg-card shadow-forest-xl border border-border">
                    <ServiceVideo
                      src={service.videoSrc}
                      poster={service.videoPoster}
                      alt={service.title}
                    />
                  </div>
                </div>
              </div>
            )
          }

          return (
            <div
              key="dendrocare"
              className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16"
            >
              <div className={cn(service.reverse ? "lg:order-2" : "")}>
                <img
                  src={service.iconSrc}
                  alt={service.title}
                  className={cn(HOME_LOGO_CLASS_INLINE, "mb-5")}
                />
                <h3 className="mb-4 text-3xl font-semibold tracking-tight text-foreground">
                  {service.title}
                </h3>
                <p
                  className="mb-6 text-base text-muted-foreground leading-[1.75]"
                  dangerouslySetInnerHTML={{ __html: service.description }}
                />
                {service.features.length > 0 && (
                  <div className="mb-8 flex flex-col gap-3">
                    {service.features.map((feature) => (
                      <FeatureCard key={feature.title} feature={feature} />
                    ))}
                  </div>
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button variant="outline" className="hover-shimmer" asChild>
                    <a
                      href={service.ctaExternalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {service.ctaExternalLabel}
                      <ExternalLink className="size-4" aria-hidden data-icon="inline-end" />
                    </a>
                  </Button>
                  <Button
                    className="hover-shimmer shadow-forest transition-all hover:scale-[1.02]"
                    onClick={() => handleButtonClick(index)}
                  >
                    {service.buttonText}
                  </Button>
                </div>
              </div>
              <div className={cn("relative", service.reverse ? "lg:order-1" : "")}>
                <div className="flex aspect-video items-center justify-center overflow-hidden rounded-2xl bg-card shadow-forest-xl border border-border">
                  <ServiceVideo
                    poster={service.imageSrc}
                    alt={service.imageAlt}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* ─── Detail Modal ──────────────────────────────────────────── */}
      {isModalOpen && selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="presentation"
          onClick={handleCloseModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />

          {/* Dialog */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="service-modal-title"
            className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-card shadow-2xl focus:outline-none"
            onClick={(e) => e.stopPropagation()}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: dialog needs focus management
            tabIndex={0}
          >
            {/* Modal header */}
            <div className="relative bg-gradient-to-br from-primary to-primary/75 p-8 pb-10">
              <button
                type="button"
                onClick={handleCloseModal}
                className="absolute right-4 top-4 rounded-full bg-white/15 p-2 backdrop-blur-sm transition-all hover:scale-110 hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                aria-label="Fermer"
              >
                <X className="size-5 text-white" aria-hidden />
              </button>
              <div className="mb-5 flex flex-wrap gap-3">
                {selectedItem.type === "merged" ? (
                  selectedItem.logos.map((logo) => (
                    <div
                      key={logo.src}
                      className="flex h-20 w-44 items-center justify-center rounded-xl bg-white px-4 sm:h-24 sm:w-52"
                    >
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ))
                ) : (
                  <div className="flex h-20 w-44 items-center justify-center rounded-xl bg-white px-4 sm:h-24 sm:w-52">
                    <img
                      src={selectedItem.iconSrc}
                      alt={selectedItem.title}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                )}
              </div>
              <h2
                id="service-modal-title"
                className="mb-2 text-2xl font-semibold text-white"
              >
                {selectedItem.title}
              </h2>
              <p
                className="text-sm text-primary-foreground/80 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedItem.description }}
              />
            </div>

            {/* Modal body */}
            <div className="p-8">
              {selectedFeatures.length > 0 && (
                <div className="mb-8 grid gap-4 md:grid-cols-2">
                  {selectedFeatures.map((feature) => {
                    const Icon = feature.icon
                    return (
                      <div
                        key={feature.title}
                        className="group rounded-xl border border-primary/15 bg-gradient-to-br from-primary/5 to-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-forest"
                      >
                        <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-primary">
                          <Icon className="size-5 text-primary-foreground" aria-hidden />
                        </div>
                        <div className="mb-1.5 flex flex-wrap items-center gap-2">
                          <h4 className="text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                            {feature.title}
                          </h4>
                          {feature.badge && (
                            <Badge variant="secondary" className="text-xs">
                              {feature.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {feature.description}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}

              <div>
                <div className="mb-4 flex items-center gap-2">
                  <Sparkles className="size-4 text-primary" aria-hidden />
                  <h3 className="text-base font-semibold text-foreground">
                    En savoir plus
                  </h3>
                </div>
                <div className="rounded-xl border border-border bg-muted p-5 text-sm">
                  {selectedItem.explore}
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <Button
                  size="lg"
                  className="shadow-forest transition-all hover:scale-[1.02]"
                  onClick={handleCloseModal}
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
